import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { User, Role } from '../store/slices/authSlice';
import type { appConfigs, LoginAppRights } from '../components/types/types';
import { setAuthData, clearAuthData } from '../store/slices/authSlice';
import type { RootState, AppDispatch } from '../store/store';

interface AuthContextType {
  user: User | null;
  role: Role[] | null;
  appConfigs: appConfigs | null;
  appRights: LoginAppRights[] | null;
  setUser: (user: User | null) => void;
  login: (username: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, token, role, superadminClientId, appConfigs, appRights } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const storedUserString = localStorage.getItem('user');
    const storedjwtToken = localStorage.getItem('jwtToken');
    const storedRoleString = localStorage.getItem('role');
    const storedSuperadminClientId = localStorage.getItem('superadminClientId');
    const storedappConfig = localStorage.getItem('appConfigs');
    const storedappRightsString = localStorage.getItem('appRights');

    if (storedUserString !== null && storedjwtToken !== null && storedRoleString !== null && storedappConfig !== null && storedappRightsString !== null) {
      try {
        const storedUser = JSON.parse(storedUserString) as User;
        const storedrole = JSON.parse(storedRoleString) as Role[];
        const storedappConfigValue = JSON.parse(storedappConfig) as appConfigs;
        const storedappRights = JSON.parse(storedappRightsString ?? '[]') as LoginAppRights[];

        dispatch(setAuthData({
          user: storedUser,
          token: storedjwtToken,
          role: storedrole,
          superadminClientId: storedSuperadminClientId,
          appConfigs: storedappConfigValue,
          appRights: storedappRights
        }));
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
      }
    }
  }, [dispatch]);

  const setUser = (newUser: User | null): void => {
    dispatch(setAuthData({ user: newUser, token, role, superadminClientId, appConfigs, appRights }));
  };

  const login = (username: string): void => {
    console.log(`Logged in with username: ${username}`);
  };

  const logout = (): void => {
    const engageConfig = Array.isArray(appConfigs)
      ? (appConfigs as { configName: string; configValue: string; configType: string }[]).find(
        (config) => config.configName === 'BaseURLFE' && config.configType === 'EngageApp'
      )
      : null;

    let redirectUrl = engageConfig?.configValue;

    // Use environment variable as a fallback if the configuration is not found
    if (redirectUrl === undefined || redirectUrl === null || redirectUrl === '') {
      redirectUrl = process.env.REACT_APP_ENGAGE_URL ?? '/';
      
      if (redirectUrl === '') {
        redirectUrl = '/';
      }
    }

    // Clear all data before redirecting
    localStorage.clear();
    sessionStorage.clear();
    dispatch(clearAuthData());

    window.location.href = redirectUrl.endsWith('/') ? redirectUrl + 'logout' : redirectUrl + '/logout';
  };

  const value = useMemo(
    () => ({
      user,
      setUser,
      login,
      logout,
      role,
      appConfigs,
      appRights,
    }),
    [user, role, appConfigs, appRights],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
